/**
 * Ahsay Password Decryption Utility
 * 
 * Decrypts AES-256-GCM encrypted passwords from the Ahsay API.
 * Algorithm: AES-256-GCM (12-byte IV, 16-byte tag)
 * 
 * Payload structure:
 * - Base64-decode encrypted string
 * - First 12 bytes = IV
 * - Last 16 bytes = tag
 * - Middle bytes = ciphertext
 */

/**
 * Decrypts an AES-256-GCM encrypted password
 * @param encryptedPassword - Base64 encoded encrypted password string
 * @returns Decrypted plaintext password (UTF-8)
 * @throws Error if decryption fails
 */
export async function decryptAhsayPassword(
  encryptedPassword: string
): Promise<string> {
  try {
    // Get encryption key from environment or use dev key
    const keyBase64 =
      process.env.REACT_APP_AHSAY_CREDENTIALS_ENCRYPTION_KEY ||
      'MTIzNDU2Nzg5MDEyMzQ1Njc4OTAxMjM0NTY3ODkwMTI=';
    
    // Base64 decode the key (should be 32 bytes for AES-256)
    const keyBuffer = Uint8Array.from(atob(keyBase64), (c) => c.charCodeAt(0));
    
    // Base64 decode the encrypted password
    const encryptedBuffer = Uint8Array.from(
      atob(encryptedPassword),
      (c) => c.charCodeAt(0)
    );
    
    // Extract IV (first 12 bytes)
    const iv = encryptedBuffer.slice(0, 12);
    
    // Extract tag (last 16 bytes)
    const tag = encryptedBuffer.slice(-16);
    
    // Extract ciphertext (middle bytes)
    const ciphertext = encryptedBuffer.slice(12, -16);
    
    // Import the key for Web Crypto API
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    
    // Combine ciphertext and tag for GCM mode
    // In GCM mode, the tag is appended to the ciphertext
    const ciphertextWithTag = new Uint8Array(ciphertext.length + tag.length);
    ciphertextWithTag.set(ciphertext);
    ciphertextWithTag.set(tag, ciphertext.length);
    
    // Decrypt using Web Crypto API
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        // eslint-disable-next-line object-shorthand
        iv: iv,
        tagLength: 128, // 16 bytes = 128 bits
      },
      cryptoKey,
      ciphertextWithTag
    );
    
    // Convert decrypted buffer to UTF-8 string
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error('Ahsay password decryption failed:', error);
    throw new Error(
      `Failed to decrypt Ahsay password: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
